import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Drawer,
  Button,
  CircularProgress,
  Avatar,
  Tab,
  Tabs,
  Divider,
  Collapse,
  Stack,
} from '@mui/material';
import {
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as FileIcon,
  Architecture as CadIcon,
  FolderZip as ZipIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Folder as FolderIcon,
  CalendarMonth as DateIcon,
  FilterAltOff as ClearFilterIcon,
  Visibility as ViewIcon,
  PlaceOutlined as PlaceIcon,
} from '@mui/icons-material';
import { projetDessinsAPI } from '../../../services/api';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import CustomTablePagination from '../../../components/CustomTablePagination';
import {
  ProjetDessin,
  ProjetDessinVersion,
  ProjetWithDessinCount,
} from '../../../types/projetDessin';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { getFullFileUrl } from '../../../utils/fileUrl';
import PermissionButton from '../../../components/PermissionButton';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import PlanFormDialog, { PlanOwner } from '../../../components/plans/PlanFormDialog';
import PlanViewerDialog from '../../../components/plans/PlanViewerDialog';
import { PlanKindChip, planMeta } from '../../../components/plans/planUi';
import { usePermissions } from '../../../contexts/PermissionContext';
import { plansKeys } from '../../../hooks/queries/usePlansTechniques';
import TousLesPlans from '../components/TousLesPlans';

const PROD_COLOR = '#FF5722';

// ── Helpers ──────────────────────────────────────────────────────

const fileTypeIcon = (mime?: string | null, name?: string) => {
  const m = (mime || '').toLowerCase();
  const n = (name || '').toLowerCase();
  if (m.startsWith('image/')) return <ImageIcon />;
  if (m === 'application/pdf' || n.endsWith('.pdf')) return <PdfIcon />;
  if (n.endsWith('.dwg') || n.endsWith('.dxf') || n.endsWith('.step') || n.endsWith('.stp') || n.endsWith('.iges') || n.endsWith('.igs')) return <CadIcon />;
  if (m === 'application/zip' || n.endsWith('.zip') || n.endsWith('.rar') || n.endsWith('.7z')) return <ZipIcon />;
  return <FileIcon />;
};

const fileTypeColor = (mime?: string | null, name?: string) => {
  const m = (mime || '').toLowerCase();
  const n = (name || '').toLowerCase();
  if (m.startsWith('image/')) return '#4CAF50';
  if (m === 'application/pdf' || n.endsWith('.pdf')) return '#E53935';
  if (n.endsWith('.dwg') || n.endsWith('.dxf')) return '#1976D2';
  if (m === 'application/zip' || n.endsWith('.zip')) return '#F57C00';
  return '#757575';
};

const formatBytes = (bytes?: number | null) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso?: string | null) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('fr-FR', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// ── Main Page ──────────────────────────────────────────────────

const DESSIN_FILTER_SCHEMA = {
  code:          { type: 'text',      param: 'code' },
  type:          { type: 'select',    param: 'type' },
  nom:           { type: 'text',      param: 'nom' },
  client_name:   { type: 'text',      param: 'client_name' },
  periode:       { type: 'dateRange', paramFrom: 'date_debut_from', paramTo: 'date_debut_to' },
  latest_upload: { type: 'dateRange', paramFrom: 'latest_upload_from', paramTo: 'latest_upload_to' },
  dessin_filter: { type: 'select',    param: 'dessin_filter' },
} as const;

const DessinsManagement: React.FC = () => {
  const [projets, setProjets] = useState<ProjetWithDessinCount[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    DESSIN_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );
  const [activeProjet, setActiveProjet] = useState<ProjetWithDessinCount | null>(null);
  const [dessins, setDessins] = useState<ProjetDessin[]>([]);
  const [dessinsLoading, setDessinsLoading] = useState(false);
  const [expandedDessinId, setExpandedDessinId] = useState<number | null>(null);
  const [onglet, setOnglet] = useState<'projets' | 'plans'>('projets');
  // the plan form (new plan for an owner, or edit / new version of a plan) and the viewer
  const [form, setForm] = useState<{ owner: PlanOwner; plan: ProjetDessin | null } | null>(null);
  const [vue, setVue] = useState<ProjetDessin | null>(null);
  const qc = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('Production', 'edit', 'Dessins');
  const canDelete = hasPermission('Production', 'delete', 'Dessins');

  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  const fetchProjets = useCallback(async () => {
    try {
      setLoading(true);
      const data = await projetDessinsAPI.listProjetsWithCounts({
        ...toQueryParams(),
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      });
      setProjets(data.items);
      setTotal(data.total);
    } catch (err) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, rowsPerPage, showError]);

  useEffect(() => {
    fetchProjets();
  }, [fetchProjets]);

  // ?type=&id=&code=&nom= (link « Plans » from the Bureau d'études): open that projet / service
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const type = params.get('type');
    const id = Number(params.get('id'));
    if ((type === 'projet' || type === 'service') && id) {
      openProjet({ type, id, code: params.get('code') || '', nom: params.get('nom') || '', dessin_count: 0 });
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openProjet = async (projet: ProjetWithDessinCount) => {
    setActiveProjet(projet);
    setExpandedDessinId(null);
    setDessinsLoading(true);
    try {
      const data = await projetDessinsAPI.listFor(projet.type, projet.id);
      setDessins(data.items);
    } catch (err) {
      showError(extractErrorMessage(err));
    } finally {
      setDessinsLoading(false);
    }
  };

  const closeDrawer = () => {
    setActiveProjet(null);
    setDessins([]);
    setExpandedDessinId(null);
  };

  const refreshDessins = async () => {
    qc.invalidateQueries({ queryKey: plansKeys.root });  // « Tous les plans »
    qc.invalidateQueries({ queryKey: ['production'] });  // the BE cards count the plans
    if (!activeProjet) return;
    const data = await projetDessinsAPI.listFor(activeProjet.type, activeProjet.id);
    setDessins(data.items);
    fetchProjets(); // refresh count chips on the table
  };

  const ownerOfActive = (): PlanOwner | null =>
    activeProjet ? { type: activeProjet.type, id: activeProjet.id, code: activeProjet.code, nom: activeProjet.nom } : null;

  const handleNewDessin = () => {
    const owner = ownerOfActive();
    if (owner) setForm({ owner, plan: null });
  };

  const handleEditPlan = (plan: ProjetDessin) => {
    const owner = plan.owner || ownerOfActive();
    if (owner) setForm({ owner: { type: owner.type, id: owner.id, code: owner.code, nom: owner.nom }, plan });
  };

  const handlePlanSaved = async () => {
    showSuccess(form?.plan ? 'Plan enregistré' : 'Plan déposé');
    setForm(null);
    await refreshDessins();
  };

  const handleDeleteDessin = (dessin: ProjetDessin) => {
    showConfirm(
      `Supprimer le plan "${dessin.title}" et toutes ses ${dessin.version_count} version(s) ?`,
      async () => {
        try {
          await projetDessinsAPI.delete(dessin.id);
          showSuccess('Plan supprimé');
          await refreshDessins();
        } catch (err) {
          showError(extractErrorMessage(err));
        }
      },
    );
  };

  const handleDeleteVersion = (dessin: ProjetDessin, version: ProjetDessinVersion) => {
    showConfirm(
      `Supprimer la version v${version.version} de "${dessin.title}" ?`,
      async () => {
        try {
          await projetDessinsAPI.deleteVersion(dessin.id, version.id);
          showSuccess('Version supprimée');
          await refreshDessins();
        } catch (err) {
          showError(extractErrorMessage(err));
        }
      },
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Plans techniques</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Plans de pliage, DXF de découpe, plans d'usinage… de chaque projet et service. Chaque plan arrive tout seul
            chez l'opérateur de l'étape ou de la machine concernée, avec ses observations.
          </Typography>
        </Box>
        {onglet === 'projets' && hasActive() && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<ClearFilterIcon />}
            onClick={reset}
          >
            Effacer les filtres
          </Button>
        )}
      </Box>

      <Tabs value={onglet} onChange={(_, v) => setOnglet(v)} sx={{ mb: 2, '& .Mui-selected': { color: `${PROD_COLOR} !important` } }}
        TabIndicatorProps={{ sx: { bgcolor: PROD_COLOR } }}>
        <Tab value="projets" label="Par projet / service" />
        <Tab value="plans" label="Tous les plans" />
      </Tabs>

      {onglet === 'plans' && <TousLesPlans onEdit={handleEditPlan} />}

      {onglet === 'projets' && (
      <TableContainer component={Paper} sx={getModuleTableTheme('production')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Code
                  <ColumnFilter
                    type="text"
                    value={filters.code}
                    onChange={(v) => update('code', v)}
                    label="Code"
                    color={PROD_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Type
                  <ColumnFilter
                    type="select"
                    value={filters.type}
                    onChange={(v) => update('type', v)}
                    label="Type"
                    color={PROD_COLOR}
                    options={[
                      { value: 'projet', label: 'Projet' },
                      { value: 'service', label: 'Service' },
                    ]}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Projet / Service
                  <ColumnFilter
                    type="text"
                    value={filters.nom}
                    onChange={(v) => update('nom', v)}
                    label="Nom du projet / service"
                    color={PROD_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Client
                  <ColumnFilter
                    type="text"
                    value={filters.client_name}
                    onChange={(v) => update('client_name', v)}
                    label="Client"
                    color={PROD_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Période
                  <ColumnFilter
                    type="dateRange"
                    value={filters.periode}
                    onChange={(v) => update('periode', v)}
                    label="Période (date début)"
                    color={PROD_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Plans
                  <ColumnFilter
                    type="select"
                    value={filters.dessin_filter}
                    onChange={(v) => update('dessin_filter', v)}
                    label="Présence de plans"
                    color={PROD_COLOR}
                    options={[
                      { value: 'avec', label: 'Avec plans' },
                      { value: 'sans', label: 'Sans plans' },
                    ]}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Dernier upload
                  <ColumnFilter
                    type="dateRange"
                    value={filters.latest_upload}
                    onChange={(v) => update('latest_upload', v)}
                    label="Dernier upload"
                    color={PROD_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress sx={{ color: PROD_COLOR }} />
                </TableCell>
              </TableRow>
            ) : projets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  Aucun projet ni service trouvé.
                </TableCell>
              </TableRow>
            ) : (
              projets.map((p) => (
                <TableRow
                  key={`${p.type}-${p.id}`}
                  hover
                  onClick={() => openProjet(p)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Chip label={p.code} size="small" sx={{ fontFamily: 'monospace', bgcolor: PROD_COLOR, color: 'white', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={p.type === 'service' ? 'Service' : 'Projet'} size="small"
                      sx={{ bgcolor: p.type === 'service' ? '#2196F3' : PROD_COLOR, color: 'white' }} />
                  </TableCell>
                  <TableCell>{p.nom}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{p.client_name || '-'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {p.date_debut ? new Date(p.date_debut).toLocaleDateString('fr-FR') : '-'}
                    {' → '}
                    {p.date_fin ? new Date(p.date_fin).toLocaleDateString('fr-FR') : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<FolderIcon sx={{ fontSize: 16 }} />}
                      label={p.dessin_count}
                      size="small"
                      color={p.dessin_count > 0 ? 'primary' : 'default'}
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {p.latest_upload_at ? formatDate(p.latest_upload_at) : <span style={{ fontStyle: 'italic' }}>jamais</span>}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FolderIcon />}
                      onClick={(e) => { e.stopPropagation(); openProjet(p); }}
                      sx={{ borderColor: PROD_COLOR, color: PROD_COLOR, '&:hover': { borderColor: PROD_COLOR, bgcolor: 'rgba(255, 87, 34, 0.04)' } }}
                    >
                      Ouvrir
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <CustomTablePagination
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>
      )}

      {/* ── Drawer ── */}
      <Drawer
        anchor="right"
        open={!!activeProjet}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 640 }, maxWidth: '100%' } }}
      >
        {activeProjet && (
          <>
            <Box sx={{ p: 2, bgcolor: PROD_COLOR, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderIcon />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{activeProjet.code}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{activeProjet.nom}</Typography>
              </Box>
              <IconButton onClick={closeDrawer} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <strong>{dessins.length}</strong> plan{dessins.length > 1 ? 's' : ''}
              </Typography>
              <PermissionButton
                moduleName="Production"
                operation="add"
                miniModuleName="Dessins"
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleNewDessin}
                sx={{ bgcolor: PROD_COLOR, '&:hover': { bgcolor: '#E64A19' } }}
              >
                Déposer un plan
              </PermissionButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              {dessinsLoading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CircularProgress sx={{ color: PROD_COLOR }} />
                </Box>
              ) : dessins.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', border: '2px dashed', borderColor: 'divider', boxShadow: 'none' }}>
                  <UploadIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {activeProjet?.type === 'service' ? 'Aucun plan pour ce service' : 'Aucun plan pour ce projet'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    Cliquez sur « Déposer un plan » : il arrivera chez l'opérateur concerné
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={1.5}>
                  {dessins.map((d) => {
                    const latest = d.latest_version;
                    const expanded = expandedDessinId === d.id;
                    const fileColor = fileTypeColor(latest?.file_type, latest?.file_name);
                    return (
                      <Paper key={d.id} sx={{ overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ bgcolor: fileColor, width: 40, height: 40 }}>
                            {fileTypeIcon(latest?.file_type, latest?.file_name)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                              <PlanKindChip kind={d.kind} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {d.title}{d.piece_ref ? ` · ${d.piece_ref}` : ''}
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {[d.machine?.code, d.travail_label, d.etape_label, planMeta(d)].filter(Boolean).join(' · ') || d.description}
                            </Typography>
                            {d.destinataires && (
                              <Typography variant="caption" sx={{ color: d.destinataires.length ? 'success.main' : 'text.disabled', display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <PlaceIcon sx={{ fontSize: 13 }} />
                                {d.destinataires.length
                                  ? d.destinataires.map((x) => `${x.label} · ${x.responsable || 'à désigner'}`).join(' ; ')
                                  : 'Pas encore de tâche sur cette machine'}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip label={`v${latest?.version || 1}`} size="small" sx={{ height: 18, fontSize: '0.7rem', fontWeight: 600, bgcolor: PROD_COLOR, color: 'white' }} />
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {formatBytes(latest?.file_size)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                · {formatDate(latest?.uploaded_at)}
                              </Typography>
                              {d.version_count > 1 && (
                                <Chip
                                  icon={<HistoryIcon sx={{ fontSize: 14 }} />}
                                  label={`${d.version_count} versions`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                            </Box>
                          </Box>
                          <Stack direction="row" spacing={0.25}>
                            <Tooltip title="Consulter le plan">
                              <IconButton size="small" sx={{ color: PROD_COLOR }} onClick={() => setVue(d)}>
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Télécharger la dernière version">
                              <IconButton size="small" component="a" href={getFullFileUrl(latest?.file_url)} target="_blank" rel="noopener noreferrer" download>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canEdit && (
                              <Tooltip title="Modifier / nouvelle version">
                                <IconButton size="small" onClick={() => handleEditPlan(d)} color="primary">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Supprimer le plan">
                                <IconButton size="small" onClick={() => handleDeleteDessin(d)} color="error">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={expanded ? 'Masquer l\'historique' : 'Voir l\'historique'}>
                              <IconButton
                                size="small"
                                onClick={() => setExpandedDessinId(expanded ? null : d.id)}
                                disabled={d.version_count <= 1}
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>

                        <Collapse in={expanded}>
                          <Divider />
                          <Box sx={{ bgcolor: 'action.hover', px: 1.5, py: 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                              HISTORIQUE
                            </Typography>
                            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                              {d.versions.map((v) => (
                                <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                                  <Chip label={`v${v.version}`} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, minWidth: 36 }} />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                                      {v.file_name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      <DateIcon sx={{ fontSize: 10, verticalAlign: 'middle' }} /> {formatDate(v.uploaded_at)} · {formatBytes(v.file_size)}
                                      {v.uploaded_by_name && ` · ${v.uploaded_by_name}`}
                                    </Typography>
                                    {v.note && (
                                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontStyle: 'italic' }}>
                                        "{v.note}"
                                      </Typography>
                                    )}
                                  </Box>
                                  <IconButton size="small" component="a" href={getFullFileUrl(v.file_url)} target="_blank" rel="noopener noreferrer" download>
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteVersion(d, v)}
                                    disabled={d.version_count <= 1 || !canDelete}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </>
        )}
      </Drawer>

      <PlanFormDialog open={!!form} owner={form?.owner || null} plan={form?.plan || null}
        onClose={() => setForm(null)} onSaved={handlePlanSaved} />
      <PlanViewerDialog plan={vue} onClose={() => setVue(null)}
        onEdit={canEdit ? (p) => { setVue(null); handleEditPlan(p); } : undefined} />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

    </Box>
  );
};

export default DessinsManagement;
